'use client'
import {Input, InputProps} from '@heroui/react'
import React, {useState} from 'react'
import {
  Control,
  useController,
  FieldValues,
  Path,
  useForm,
} from 'react-hook-form'

import Box from '@/components/UI/Box/Box'
import {Body7} from '@/components/UI/Typography'

import {cn} from '@/src/shared/utils/twMerge'

interface AmyInputProps<T extends FieldValues>
  extends Omit<InputProps, 'name' | 'value' | 'onChange'> {
  name?: Path<T>
  control?: Control<T>
  value?: string
  onChange?: (value: string) => void
  error?: string
  isControlled?: boolean
}

const AmyInput = <T extends FieldValues>({
  name,
  control,
  value: externalValue,
  onChange: externalOnChange,
  placeholder,
  disabled = false,
  className = '',
  error,
  isControlled = false,
  type = 'text',
  fullWidth = false,
  ...rest
}: AmyInputProps<T>) => {
  const [uncontrolledValue, setUncontrolledValue] = useState<string>('')

  // Create a default form control to prevent undefined errors
  const defaultForm = useForm<T>()

  // Only use useController when we have proper control and name
  const shouldUseController = isControlled && name && control

  const controllerResult = useController({
    name: name || ('' as Path<T>),
    control: control || defaultForm.control,
    disabled: !shouldUseController,
  })

  const controlledField = shouldUseController ? controllerResult : null

  const value = isControlled
    ? controlledField?.field.value
    : (externalValue ?? uncontrolledValue)

  const handleChange = (value: string) => {
    if (isControlled) {
      controlledField?.field.onChange(value)
    } else {
      if (externalOnChange) {
        externalOnChange(value)
      } else {
        setUncontrolledValue(value)
      }
    }
  }

  const fieldError = isControlled ? controlledField?.fieldState.error : null
  const displayError = fieldError?.message || error

  return (
    <Box
      className={cn(
        'relative flex-col gap-1',
        fullWidth && 'w-full',
        className
      )}>
      <Input
        type={type}
        value={value || ''}
        onValueChange={handleChange}
        placeholder={placeholder || ' '}
        isDisabled={disabled}
        variant="bordered"
        labelPlacement="outside"
        isInvalid={!!displayError}
        errorMessage={displayError || ''}
        classNames={{
          input: cn(
            className,
            'text-[14px]',
            '!border-none',
            '!ring-offset-0',
            'outline-none'
          ),
          label: 'text-[14px]',
          base: 'focus:border-none focus:ring-0',
        }}
        className={className}
        {...rest}
      />
      <Box className="absolute right-1 -bottom-5">
        {rest.maxLength && (
          <Body7 className="text-gray-400">
            {value?.length || 0} /{rest.maxLength}
          </Body7>
        )}
      </Box>
    </Box>
  )
}

export default AmyInput
